import { useState } from "react";
import * as XLSX from "xlsx";
import { AlertTriangle, CheckCircle2, FileText, Upload, X } from "lucide-react";
import { money } from "../lib/finance";
import { assessConfidence } from "../lib/pdfTemplates";
import { Modal } from "./common";

export function PdfImportModal({ parseFn, onFinalize, onClose }) {
  const [file, setFile] = useState(null);
  const [ocr, setOcr] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [parsed, setParsed] = useState(null);   // when non-null → preview stage
  const [original, setOriginal] = useState(null); // snapshot of fields as parsed from PDF (for cross-check)
  const [conf, setConf] = useState(null);

  const runExtract = async () => {
    if (!file) return;
    setBusy(true);
    setError("");
    setProgress(0);
    try {
      const data = await parseFn(file, ocr, setProgress);
      setParsed(data);
      setOriginal({ ...data });
      setConf(assessConfidence(data));
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const updateField = (key, value) => {
    setParsed((p) => {
      const next = { ...p, [key]: value };
      setConf(assessConfidence(next));
      return next;
    });
  };

  const goBack = () => { setParsed(null); setOriginal(null); setConf(null); };
  const finalize = () => onFinalize({ ...parsed }, file);

  // Compare current value with original parsed value for mismatch warning.
  const mismatchHint = (key, currentValue) => {
    if (!original) return null;
    const orig = original[key];
    if (orig === undefined || orig === null || orig === "") return null;
    if (key === "premium") {
      const o = Number(orig) || 0;
      const c = Number(currentValue) || 0;
      if (o === 0) return null;
      const diff = Math.abs(c - o);
      const pct = (diff / o) * 100;
      if (pct > 2) {
        return `PDF'den okunan brüt: ${o.toLocaleString("tr-TR")} ₺ · Fark: ${pct.toFixed(1)}%`;
      }
      return null;
    }
    if (String(orig).trim() !== String(currentValue || "").trim()) {
      const sorig = String(orig).slice(0, 40);
      return `PDF'den okunan: ${sorig}`;
    }
    return null;
  };

  const rows = parsed && [
    { key: "company", label: "Sigorta Şirketi", type: "text" },
    { key: "policeNo", label: "Poliçe No", type: "text" },
    { key: "customer", label: "Sigortalı", type: "text" },
    { key: "branch", label: "Branş", type: "text" },
    { key: "start", label: "Başlangıç", type: "date" },
    { key: "end", label: "Bitiş", type: "date" },
    { key: "premium", label: "Brüt Prim", type: "number" },
  ];
  const lowCount = conf ? Object.values(conf).filter((v) => v === "low").length : 0;

  return (
    <Modal title="PDF’den poliçe aktar" onClose={onClose}>
      <div className="pdf-import">
        {!parsed && (
          <>
            <div className="upload-zone">
              <FileText size={30} />
              <strong>Poliçe PDF’sini seçin</strong>
              <span>Doğa, Anadolu, Allianz, Aksigorta, HDI, Sompo, MAPFRE, ANA, EMAA, Türkiye, AXA, Ray, Neova, Zurich, Groupama, Ankara, Quick ve diğerleri için otomatik alan çıkarımı yapılır.</span>
              <input data-testid="policy-pdf-input" type="file" accept="application/pdf,.pdf" onChange={(e) => { setError(""); setFile(e.target.files?.[0] || null); }} />
              {file && <b>{file.name} · {(file.size / 1024).toFixed(0)} KB</b>}
            </div>
            <label className="ocr-toggle">
              <input data-testid="pdf-ocr-checkbox" type="checkbox" checked={ocr} onChange={(e) => setOcr(e.target.checked)} /> Taranmış PDF’lerde yerel OCR kullan (yavaş ama gerekli)
            </label>
            {busy && <div className="pdf-progress" data-testid="pdf-progress">İşleniyor… {progress > 0 ? `%${progress}` : "PDF açılıyor"}</div>}
            {error && (
              <div className="auth-error" data-testid="pdf-error" style={{ marginTop: 12, alignItems: "flex-start" }}>
                <span style={{ lineHeight: 1.5 }}>{error}</span>
              </div>
            )}
            <p className="privacy-note">PDF dosyası sunucuya gönderilmez; alanlar tarayıcı tarafında çıkarılır. Bir sonraki adımda önizleyip düşük güvenli alanları düzeltebilirsiniz.</p>
            <div className="modal-actions">
              <button className="outline-button" data-testid="pdf-import-cancel" onClick={onClose}>Vazgeç</button>
              <button className="primary-button" data-testid="pdf-import-submit" disabled={!file || busy} onClick={runExtract}>{busy ? "İşleniyor…" : "Alanları çıkar"}</button>
            </div>
          </>
        )}

        {parsed && (
          <div className="pdf-preview" data-testid="pdf-preview">
            <div className="pdf-preview-head">
              <strong>Çıkarılan alanlar</strong>
              <span className={`pdf-preview-badge ${lowCount ? "warn" : "ok"}`} data-testid="pdf-preview-badge">
                {lowCount ? `${lowCount} alanı gözden geçirin` : "Tüm alanlar hazır"}
              </span>
            </div>
            <p className="pdf-preview-hint">Sarıya boyalı alanlar düşük güvenle çıkarıldı — kaydetmeden önce düzeltmek isteyebilirsiniz. Diğer alanlar da düzenlenebilir.</p>
            <div className="pdf-preview-grid">
              {rows.map((r) => {
                const isLow = conf?.[r.key] === "low";
                const mismatch = mismatchHint(r.key, parsed[r.key]);
                return (
                  <label key={r.key} className={`pdf-preview-row ${isLow ? "low" : ""} ${mismatch ? "mismatch" : ""}`} data-testid={`pdf-preview-row-${r.key}`}>
                    <span className="pdf-preview-label">
                      {r.label}
                      {isLow && <span className="pdf-preview-low-chip">gözden geçir</span>}
                      {mismatch && <span className="pdf-preview-mismatch-chip" data-testid={`pdf-mismatch-${r.key}`} title={mismatch}><AlertTriangle size={10} /> PDF farklı</span>}
                    </span>
                    <input
                      type={r.type}
                      value={parsed[r.key] ?? ""}
                      onChange={(e) => updateField(r.key, e.target.value)}
                      data-testid={`pdf-preview-input-${r.key}`}
                    />
                    {mismatch && <em className="pdf-mismatch-hint" style={{ display: "block", color: "#b45309", fontSize: 10, marginTop: 4 }}>{mismatch}</em>}
                  </label>
                );
              })}
            </div>
            <div className="modal-actions">
              <button className="outline-button" data-testid="pdf-preview-back" onClick={goBack}>← Farklı dosya</button>
              <button className="primary-button" data-testid="pdf-preview-finalize" onClick={finalize}>Poliçe formuna geç</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}


export function ExcelImportModal({ branches, onImport, onClose }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Excel cell → ISO date. Handles dd.mm.yyyy, dd/mm/yyyy, yyyy-mm-dd, and Excel serial numbers.
  const toIsoDate = (v) => {
    if (v == null || v === "") return "";
    if (typeof v === "number") {
      // Excel serial date (days since 1899-12-30)
      const d = new Date(Math.round((v - 25569) * 86400000));
      if (isNaN(d)) return "";
      return d.toISOString().slice(0, 10);
    }
    const s = String(v).trim();
    const m1 = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (m1) return `${m1[3]}-${m1[2].padStart(2, "0")}-${m1[1].padStart(2, "0")}`;
    const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
    return "";
  };

  const toNumber = (v) => {
    if (v == null || v === "") return 0;
    if (typeof v === "number") return v;
    const s = String(v).replace(/[^0-9,.\-]/g, "");
    const hasComma = s.includes(",");
    const hasDot = s.includes(".");
    let clean = s;
    if (hasComma && hasDot) {
      const lastComma = s.lastIndexOf(",");
      const lastDot = s.lastIndexOf(".");
      clean = lastComma > lastDot ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
    } else if (hasComma) {
      clean = s.replace(",", ".");
    }
    const n = Number(clean);
    return isNaN(n) ? 0 : n;
  };

  const rateFor = (branchName) => {
    const b = branches.find((x) => x.name === branchName);
    return b?.commissionRate ?? 15;
  };

  const parseFile = async (f) => {
    setError("");
    setResult(null);
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!data.length) {
        setError("Dosya boş veya kolonlar tanınmadı");
        return;
      }
      // Map from Turkish column headers to our fields. Case/space-insensitive.
      const H = (r, ...keys) => {
        for (const k of Object.keys(r)) {
          const nk = k.toLocaleLowerCase("tr").replace(/\s+/g, "");
          if (keys.some((x) => x.toLocaleLowerCase("tr").replace(/\s+/g, "") === nk)) return r[k];
        }
        return "";
      };
      const mapped = data.map((r) => {
        const branch = String(H(r, "Branş", "Branch") || "").trim();
        return {
          policeNo: String(H(r, "PoliçeNo", "PoliceNo", "Poliçe Numarası") || "").trim(),
          customer: String(H(r, "Müşteri", "Musteri", "Müşteri Adı", "Ad Soyad") || "").trim(),
          branch,
          company: String(H(r, "Şirket", "Sirket", "Sigorta Şirketi") || "").trim(),
          plate: String(H(r, "Plaka", "Plate", "Araç Plakası", "Plaka No") || "").trim().toUpperCase(),
          start: toIsoDate(H(r, "Başlangıç", "Baslangic", "Başlangıç Tarihi", "Start")),
          end: toIsoDate(H(r, "Bitiş", "Bitis", "Bitiş Tarihi", "End")),
          premium: toNumber(H(r, "Brüt Prim", "Brut Prim", "Prim", "Premium")),
          netPremium: (() => { const v = toNumber(H(r, "Net Prim", "Net Premium", "Net")); return v > 0 ? v : null; })(),
          commissionRate: toNumber(H(r, "Komisyon %", "Komisyon", "Branş %", "Rate")) || rateFor(branch),
          payment: String(H(r, "Ödeme", "Odeme", "Ödeme Durumu") || "Bekliyor").trim() || "Bekliyor",
          note: String(H(r, "Not", "Notlar", "Note") || "").trim(),
        };
      });
      setRows(mapped);
    } catch (e) {
      setError(`Excel okunamadı: ${e?.message || e}`);
    }
  };

  const submit = async () => {
    if (!rows.length) return;
    // Basic validation: require policeNo + branch
    const valid = rows.filter((r) => r.policeNo && r.branch);
    const skipped = rows.length - valid.length;
    if (!valid.length) {
      setError("Geçerli kayıt yok. Poliçe No ve Branş kolonları zorunlu.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await onImport(valid);
      setResult({ ...r, skipped });
    } catch (e) {
      setError(`İçe aktarım başarısız: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const sample = [{
      "Poliçe No": "TRF-2026-001",
      "Müşteri": "Ahmet Yılmaz",
      "Branş": "Trafik",
      "Şirket": "Anadolu Sigorta",
      "Plaka": "34 ABC 123",
      "Başlangıç": "01.01.2026",
      "Bitiş": "01.01.2027",
      "Brüt Prim": 4500,
      "Net Prim": "",
      "Komisyon %": 10,
      "Ödeme": "Ödendi",
      "Not": "",
    }, {
      "Poliçe No": "KSK-2026-002",
      "Müşteri": "Ayşe Demir",
      "Branş": "Kasko",
      "Şirket": "Allianz",
      "Plaka": "06 XYZ 4567",
      "Başlangıç": "15.02.2026",
      "Bitiş": "15.02.2027",
      "Brüt Prim": 12500,
      "Net Prim": 11000,
      "Komisyon %": 15,
      "Ödeme": "Bekliyor",
      "Not": "Kasko için manuel net prim",
    }];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sample), "Poliçeler");
    XLSX.writeFile(wb, "sigortacrm-poliçe-şablonu.xlsx");
  };

  return (
    <Modal title="Excel'den toplu poliçe yükle" onClose={onClose}>
      <div className="pdf-import">
        {!result && (
          <>
            <div className="upload-zone">
              <Upload size={30} />
              <strong>Excel dosyanızı seçin (.xlsx / .xls)</strong>
              <span>Zorunlu kolonlar: Poliçe No, Branş. Diğer kolonlar: Müşteri, Şirket, Plaka, Başlangıç, Bitiş, Brüt Prim, Net Prim, Komisyon %, Ödeme, Not. (Net Prim boş bırakılırsa Brüt / netDivisor formülünden otomatik hesaplanır.)</span>
              <input data-testid="excel-file-input" type="file" accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); parseFile(f); }} />
              {file && <b>{file.name} · {(file.size / 1024).toFixed(0)} KB</b>}
            </div>
            <button className="text-button" data-testid="excel-template-download" onClick={downloadTemplate} type="button" style={{ marginBottom: 10 }}>Örnek şablon indir</button>
            {rows.length > 0 && (
              <div className="excel-preview" data-testid="excel-preview">
                <div className="excel-preview-stats"><strong>{rows.length}</strong> satır okundu · Kayıt oluşturulacak: <strong>{rows.filter((r) => r.policeNo && r.branch).length}</strong></div>
                <div className="table-scroll" style={{ maxHeight: 220 }}>
                  <table className="matrix-table">
                    <thead><tr><th>POLİÇE NO</th><th>MÜŞTERİ</th><th>BRANŞ</th><th>ŞİRKET</th><th>PLAKA</th><th>BAŞLANGIÇ</th><th>BİTİŞ</th><th>BRÜT</th></tr></thead>
                    <tbody>
                      {rows.slice(0, 20).map((r, i) => (
                        <tr key={i} className={r.policeNo && r.branch ? "" : "muted-cell"}>
                          <td>{r.policeNo || "—"}</td>
                          <td>{r.customer || "—"}</td>
                          <td>{r.branch || "—"}</td>
                          <td>{r.company || "—"}</td>
                          <td>{r.plate ? <span className="plate-chip">{r.plate}</span> : "—"}</td>
                          <td>{r.start || "—"}</td>
                          <td>{r.end || "—"}</td>
                          <td>{r.premium ? money(r.premium) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length > 20 && <p className="privacy-note">İlk 20 satır önizleniyor. Yükleme tümünü içerir.</p>}
              </div>
            )}
            {error && <div className="auth-error" data-testid="excel-error"><X size={14} />{error}</div>}
            <div className="modal-actions">
              <button className="outline-button" data-testid="excel-import-cancel" onClick={onClose}>Vazgeç</button>
              <button className="primary-button" data-testid="excel-import-submit" disabled={!rows.length || busy} onClick={submit}>{busy ? "Yükleniyor…" : `${rows.filter((r) => r.policeNo && r.branch).length} poliçe kaydet`}</button>
            </div>
          </>
        )}
        {result && (
          <div className="auth-sent" data-testid="excel-result">
            <CheckCircle2 size={44} />
            <p><strong>{result.createdPolicies}</strong> poliçe başarıyla eklendi.</p>
            <p className="auth-sent-hint">{result.createdCustomers} yeni müşteri oluşturuldu · {result.skipped || 0} satır atlandı</p>
            <button className="primary-button" data-testid="excel-result-close" onClick={onClose}>Kapat</button>
          </div>
        )}
      </div>
    </Modal>
  );
}
